import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable, Subscriber } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { OrderService } from "./order.service";
import { PaymentMethod } from "../models/paymentmethod";
import { ConfigService } from "./config.service";

interface PaymentMethodApiItem {
  id: string;
  name: string;
  priceAbsolute?: string;
  pricePercentage?: string;
  min?: string;
  max?: string;
  articleId: string;
}

interface PaymentMethodsResponse {
  data: PaymentMethodApiItem[];
}

@Injectable({
  providedIn: "root",
})
export class PaymentService {
  transform: (response: PaymentMethodsResponse) => PaymentMethod[];
  paymentMethods: PaymentMethod[] = [];
  private PAYMENT_METHODS_URL = "/paymentMethods";

  private http = inject(HttpClient);
  private orderService = inject(OrderService);
  private configService = inject(ConfigService);

  constructor() {
    this.transform = (response: PaymentMethodsResponse): PaymentMethod[] => {
      const paymentMethods: PaymentMethod[] = response.data.map((item) => ({
        id: item.id,
        name: item.name,
        priceAbsolute: item.priceAbsolute ?? "0",
        pricePercentage: item.pricePercentage ?? null,
        min: item.min ?? null,
        max: item.max ?? null,
        articleId: item.articleId,
        image: this.getPaymentMethodImage(item.id),
      }));
      this.paymentMethods = paymentMethods;
      return paymentMethods;
    };
  }

  getPaymentMethodImage(paymentMethodId: string): string {
    switch (paymentMethodId.toUpperCase()) {
      case "ID":
        return "./assets/img/iDEAL.png";
      case "CR":
        return "./assets/img/creditcard.png";
      case "GI":
        return "./assets/img/giropay.png";
      case "MI":
      case "BA":
        return "./assets/img/bancontact.png";
      case "SO":
        return "./assets/img/sofort.png";
      case "PP":
        return "./assets/img/paypal.png";
      default:
        return "";
    }
  }

  protected catchError(error: HttpErrorResponse) {
    if (error.status !== 404 && error.status < 500) {
      console.error(error);
      const err = new Error();
      err.name = error.name;
      err.message = error.error;
    }
  }

  getPaymentMethods() {
    const request: Observable<PaymentMethodsResponse> = this.http.get<PaymentMethodsResponse>(
      this.configService.getApiUrl() + this.PAYMENT_METHODS_URL,
    );
    return request.pipe(
      catchError((error: HttpErrorResponse) => {
        this.catchError(error);

        return new Observable<PaymentMethod[]>((subscriber: Subscriber<PaymentMethod[]>) => {
          try {
            subscriber.error(this.transform(error as unknown as PaymentMethodsResponse));
          } catch {
            subscriber.error(error);
          }
        });
      }),
      map((res: PaymentMethodsResponse) => {
        return this.transform(res);
      }),
    );
  }

  toFormGroup(paymentMethods: PaymentMethod[]) {
    const group: { [key: string]: UntypedFormControl } = {};

    paymentMethods.forEach((paymentMethod) => {
      const state = paymentMethod.id === this.orderService.getPaymentMethod();

      group[paymentMethod.id] = new UntypedFormControl(
        state,
        Validators.required,
      );
    });

    return new UntypedFormGroup(group);
  }

  getCosts(paymentMethodId: string): number {
    let foundMethod: PaymentMethod = null;

    this.paymentMethods.forEach((method) => {
      if (method.id === paymentMethodId) {
        foundMethod = method;
      }
    });
    let totalCosts = 0;

    const productTotal = this.orderService.getProductTotal();
    if (Number(foundMethod.pricePercentage) > 0.0) {
      totalCosts += (Number(foundMethod.pricePercentage) / 100) * productTotal;
    }

    if (Number(foundMethod.priceAbsolute) > 0.0) {
      totalCosts += Number(foundMethod.priceAbsolute);
    }

    const minimumAmount: number = foundMethod.min ? +foundMethod.min : 0;
    const maximumAmount: number = foundMethod.max ? +foundMethod.max : 0;

    if (totalCosts < minimumAmount && minimumAmount !== 0) {
      totalCosts = minimumAmount;
    }

    if (totalCosts > maximumAmount && maximumAmount !== 0) {
      totalCosts = maximumAmount;
    }
    return totalCosts;
  }
}
